
import ns from 'ns';
import $ from 'jquery';
import Api from './Api';
import Promise from 'Promise';
import Store from 'store';

// Local storage keys
const PROFILE_RESULT_KEY = 'user.profile_result';
const DOMAIN_RESULT_PARTIAL_KEY = 'user.domain_result';

// Default user infos to use if user is not logged in.
const defaultUserInfos = {
	utilisateurId: null,
	prenom: ns.defaultUserDisplayName,
	utilisateurUdeM: false,
};

/**
 * Outputs data in HTML
 * 
 * @param {Object} data
 */
const parseUserInPage = (data) => {
	$('.user-name').html(data.prenom);
};

/**
 * Loads the user data from the server. If we already loaded it
 * returns the previous Promise, resolving with the server response.
 * 
 * @Promise {Object} res - Response from the server
 * 
 * @return {Promise}
 */
const loadFromApi = (() => {
	let loadedDeferred;
	return () => {
		if (loadedDeferred) return loadedDeferred;
		return Api.getUser();
	};
})();

/**
 * The User object is only created once and is used throughout the 
 * application. It is used to get or set data from and to the local 
 * storage and the database.
 */
const User = {
	// Data accessible by the templates
	_data: {},

	// Data map for things we do not want to send to the database.
	unwantedData: new Map([
		['requested_profile_result', null],
		['show_rollback', null],
		['instructions_shown', { instructions_shown: false }]
	]),

	// Then-able object to check if we are already saving to the database
	isSaving: Promise.resolve(),

	/**
	 * Sets data to be accessible by the templates
	 * 
	 * @param {Object} data
	 */
	setData(data) {
		this._data = data;

		this.id = data.utilisateurId;
		this.isAccountLogged = !!this.id;

		parseUserInPage(data);
	},
	
	/**
	 * Returns the User id.
	 * 
	 * @return {Number} user.id
	 */
	getId() {
		return this.id;
	},

	/**
	 * Checks if the User is logged in the UdeM environment.
	 * 
	 * @return {Boolean} User.isAccountLogged
	 */
	isLogged() {
		return this.isAccountLogged;
	},

	/**
	 * Checks if the User has a result for the domain test. If he 
	 * does, then he is surely coming back to the test.
	 * 
	 * @return {Boolean}
	 */
	isReturning() {
		return this.get(PROFILE_RESULT_KEY) !== null;
	},

	/**
	 * Gets the translations for domains and categories in the current
	 * language. This function is only called when a User is returning
	 * and took the quiz in another language than the current one.
	 *
	 * The Promise returned executes a function which stores the new
	 * values in the User, sets the current language and resolves with 
	 * the User object.
	 *
	 * @Promise {Object} User
	 * 
	 * @return {Promise}
	 */
	getDifferentLangFamilies() {
		return Api.request(`domains/${ns.lang}`).then((domains) => {
			// Set each domain's families in the correct language.
			Store.forEach((key, val) => {
				if (key.indexOf(DOMAIN_RESULT_PARTIAL_KEY) > -1) {
					const _val = typeof val === 'string' ? JSON.parse(val) : val;
					const id = key.charAt(key.length - 1);
					const domain = domains.filter(d => d.id === id)[0];
					const newFamilies = domain.familles.map(f => Object.assign({}, f));

					const newDomain = Object.assign(
						{},
						_val.domain,
						domain,
						{
							letter: ns.domainLetters.get(`${domain.id}_${ns.lang}`),
							slug: ns.domainSlugs.get(`${domain.id}_${ns.lang}`),
						}
					);

					newFamilies.forEach((family) => {
						const newName = family.nom;
						_val.families.filter(f => f.id == family.id)[0].name = newName;
					});

					_val.domain = newDomain;

					this.set(key, _val, false);
				}

				// Set each domain
				if (key === PROFILE_RESULT_KEY) {
					const _val = typeof val === 'string' ? JSON.parse(val) : val;
					const newVal = _val.map((domain) => {
						const loadedDomain = domains.filter(d => d.id === domain.id)[0];
						const newDomain = Object.assign(
							{},
							domain,
							loadedDomain,
							{
								letter: ns.domainLetters.get(`${domain.id}_${ns.lang}`),
								slug: ns.domainSlugs.get(`${domain.id}_${ns.lang}`),
							}
						);

						return newDomain
					});

					this.set(key, newVal, false);
				}
			});

			this.set('lang', { lang: ns.lang });

			return this;
		});
	},

	/**
	 * Calls the server to get the saved test from the database. The
	 * Promise executes a function where it parses the data, clears 
	 * the local storage and then saves it. The Promise resolves with 
	 * the User object.
	 *
	 * @Promise {Object} User
	 * @Reject {void} message - Console logs an error
	 * 
	 * @return {Promise}
	 */
	loadSavedData() {
		// Get cursus if we're logged in
		return Api.getCursus().then((res) => {
			// jo ! const savedCursus = JSON.parse(res).data;
			const savedCursus = JSON.parse(res);
			Store.clear();

			Object.keys(savedCursus).forEach((k) => {
				//Store.set(k, savedCursus[k]);
				Store.set(k, JSON.parse(savedCursus[k]));
			});

			return this;
		}).catch(() => {
			console.log('User is not logged in');
		});
	},

	/**
	 * Loads the User from the UdeM API. If the User is logged in, we
	 * get the saved data from the DB to be sure we have the latest 
	 * test results.
	 *
	 * In any case sets the user infos, may they be from the server
	 * or the defaultUserInfos object.
	 *
	 * Returns a Promise that can resolve with 2 values,
	 * if the user is not logged in, it resolves or fails with the 
	 * User object. If the user is logged in, it resolves with the 
	 * result of the User.loadSavedData function.
	 * 
	 * @Promise {Promise|Object} varied - User.loadSavedData's return
	 * 	value or the User object itself.
	 * @Reject {Object} User
	 *
	 * @return {Promise}
	 */
	onReady() {
		return loadFromApi().then((userInfo) => {
			if (!userInfo) {
				this.setData(defaultUserInfos);
				return this;
			} else {
				this.setData(JSON.parse(userInfo));
				return this.loadSavedData();
			}
		}).catch((res) => {
			this.setData(defaultUserInfos);
			return this;
		});
	},

	/**
	 * Gets a value from the localStorage for the requested key.
	 *
	 * @param {String} key
	 *
	 * @return {*} Value of the localStorage for requested key. Null if it doesn't exist.
	 */
	get(key) {
		if (!Store.has(key)) {
			return null;
		}

		if (typeof Store.get(key) === 'string') {
			Store.set(key, JSON.parse(Store.get(key)), false);
		}

		return Store.get(key);
	},

	/**
	 * Sets a value in the localStorage for a desired key. If the 
	 * user is logged in and the save param is set to true, it will 
	 * save the whole test results to the database. 
	 *
	 * @param {String} key
	 * @param {*} data
	 * @param {Boolean} [save=true]
	 */
	set(key, data, save = true) {
		Store.set(key, data);

		if (this.isLogged() && save) {
			this.isSaving = this.isSaving.then(() => {
				// Remove unwanted data from localStorage
				this.filterUnwantedData(true);

				// Stringify the localStorage
				const cursusData = JSON.stringify(localStorage);

				// Put unwanted data back in the localStorage
				this.filterUnwantedData(false);
//console.log(cursusData);

				return Api.setCursus(cursusData);
			});
		}
	},

	/**
	 * Filters unwanted data from the localStorage or puts it back in
	 * depending on the remove parameter. 
	 *
	 * @param {Boolean} [remove=false]
	 */
	filterUnwantedData(remove = false) {
		if (remove) {
			this.unwantedData.forEach((value, key) => {
				this.unwantedData.set(key, Store.get(key));
				Store.remove(key);
			});
		} else {
			this.unwantedData.forEach((value, key) => {
				Store.set(key, value);
			});
		}
	},

	/**
	 * Destroys the user results completely from the localStorage and
	 * from the database. 
	 */
	destroy() {
		Store.clear();
		Api.setCursus('');
	},
};

// Comment out for prod
//window.user = User;

export default User;
